import React, { useRef, useState, memo, useCallback } from 'react';
import { Group, Rect, Text } from 'react-konva';

export const StickyNote = memo(({ 
  id,
  x, 
  y, 
  text, 
  color = '#fef08a',
  width = 200,
  height = 200,
  isSelected,
  onSelect,
  onChange,
  onDragEnd
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const textRef = useRef();
  const groupRef = useRef();

  // Memoized callbacks
  const handleSelect = useCallback(() => {
    onSelect(id);
  }, [id, onSelect]);

  const handleDragEnd = useCallback((e) => {
    onDragEnd(id, {
      x: e.target.x(),
      y: e.target.y(),
    });
  }, [id, onDragEnd]);

  const handleChange = useCallback((changes) => {
    onChange(id, changes);
  }, [id, onChange]);

  const handleDoubleClick = () => {
    setIsEditing(true);
    const group = groupRef.current;
    if (!group) return;
    
    const stage = group.getStage();
    if (!stage) return;
    
    const container = stage.container();

    // Create textarea
    const textarea = document.createElement('textarea');
    document.body.appendChild(textarea); // Append to body instead of container

    const groupPosition = group.getAbsolutePosition();
    const stageBox = container.getBoundingClientRect();
    const scale = stage.scaleX(); // Get current zoom level

    textarea.value = text || '';
    textarea.style.position = 'absolute';
    textarea.style.top = groupPosition.y + stageBox.top + 10 * scale + 'px';
    textarea.style.left = groupPosition.x + stageBox.left + 10 * scale + 'px';
    textarea.style.width = (width - 20) * scale + 'px';
    textarea.style.height = (height - 20) * scale + 'px';
    textarea.style.fontSize = Math.max(12, 14 * scale) + 'px';
    textarea.style.lineHeight = '1.4';
    textarea.style.border = 'none';
    textarea.style.padding = 5 * scale + 'px';
    textarea.style.margin = '0px';
    textarea.style.overflow = 'auto';
    textarea.style.background = color;
    textarea.style.outline = '2px solid #3d9168';
    textarea.style.resize = 'none';
    textarea.style.fontFamily = 'Arial, sans-serif';
    textarea.style.transformOrigin = 'left top';
    textarea.style.textAlign = 'left';
    textarea.style.color = '#000';
    textarea.style.zIndex = '10000'; // Higher z-index
    textarea.style.boxSizing = 'border-box';
    textarea.style.wordWrap = 'break-word';

    // Ensure textarea is visible and ready
    setTimeout(() => {
      textarea.focus();
      textarea.select();
    }, 10);

    const removeTextarea = () => {
      if (textarea.parentNode) {
        textarea.parentNode.removeChild(textarea);
      }
      setIsEditing(false);
    };

    const handleBlur = () => {
      const newText = textarea.value;
      handleChange({ text: newText });
      removeTextarea();
    };

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        removeTextarea();
        e.preventDefault();
      }
      // Stop propagation to prevent canvas shortcuts (including delete/backspace)
      e.stopPropagation();
    };

    // Prevent mousedown from bubbling to prevent focus issues
    const handleMouseDown = (e) => {
      e.stopPropagation();
    };

    textarea.addEventListener('blur', handleBlur);
    textarea.addEventListener('keydown', handleKeyDown);
    textarea.addEventListener('mousedown', handleMouseDown);

    // Cleanup function
    return () => {
      textarea.removeEventListener('blur', handleBlur);
      textarea.removeEventListener('keydown', handleKeyDown);
      textarea.removeEventListener('mousedown', handleMouseDown);
    };
  };

  return (
    <Group
      ref={groupRef}
      x={x}
      y={y}
      draggable
      onClick={handleSelect}
      onTap={handleSelect}
      onDblClick={handleDoubleClick}
      onDblTap={handleDoubleClick}
      onDragEnd={handleDragEnd}
    >
      {/* Shadow */}
      <Rect
        width={width}
        height={height}
        fill="rgba(0,0,0,0.1)"
        cornerRadius={2}
        offsetX={-4}
        offsetY={-4}
      />
      
      {/* Main sticky note */}
      <Rect
        width={width}
        height={height}
        fill={color}
        stroke={isSelected ? '#3d9168' : '#d4d4d4'}
        strokeWidth={isSelected ? 3 : 1}
        cornerRadius={2}
        shadowColor="black"
        shadowBlur={5}
        shadowOpacity={0.2}
        shadowOffsetX={2}
        shadowOffsetY={2}
      />

      {/* Text */}
      {!isEditing && (
        <Text
          ref={textRef}
          text={text || 'Double-click to edit'}
          x={10}
          y={10}
          width={width - 20}
          height={height - 20}
          fontSize={14}
          fontFamily="Arial"
          fill={text ? '#000' : '#666'}
          align="left"
          verticalAlign="top"
          wrap="word"
        />
      )}
    </Group>
  );
});
